import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { t } from '../i18n';
import { getBackendUrl } from '../services/runtimeConfig';

const PreferencesContext = createContext(null);

const API_URL = getBackendUrl() + '/api';

// Currency symbols
const CURRENCY_SYMBOLS = {
  EUR: '€',
  USD: '$',
  XOF: 'CFA'
};

// Default exchange rates (EUR as base)
const DEFAULT_RATES = {
  EUR: 1.0,
  USD: 1.08,
  XOF: 655.957
};

export const PreferencesProvider = ({ children }) => {
  const { user } = useAuth();
  const [language, setLanguage] = useState(user?.language || 'fr');
  const [currency, setCurrency] = useState(user?.currency || 'EUR');
  const [exchangeRates, setExchangeRates] = useState(DEFAULT_RATES);
  const [currencies, setCurrencies] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch exchange rates and available options on mount
  useEffect(() => {
    fetchExchangeRates();
  }, []);

  // Update preferences when user changes
  useEffect(() => {
    if (user) {
      setLanguage(user.language || 'fr');
      setCurrency(user.currency || 'EUR');
    }
  }, [user]);

  const fetchExchangeRates = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/exchange-rates`, { _silent: true });
      setExchangeRates(response.data.rates);
      setCurrencies(response.data.currencies);
      setLanguages(response.data.languages);
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      // Use defaults
      setCurrencies([
        { code: 'EUR', name: 'Euro', symbol: '€' },
        { code: 'USD', name: 'Dollar américain', symbol: '$' },
        { code: 'XOF', name: 'Franc CFA (BCEAO)', symbol: 'CFA' }
      ]);
      setLanguages([
        { code: 'fr', name: 'Français' },
        { code: 'en', name: 'English' }
      ]);
    }
  };

  // Save preferences to backend
  const savePreferences = async (newLanguage, newCurrency) => {
    setLoading(true);
    try {
      const response = await axios.put(`${API_URL}/auth/preferences`, {
        language: newLanguage,
        currency: newCurrency
      });
      setLanguage(response.data.language);
      setCurrency(response.data.currency);
      return true;
    } catch (error) {
      console.error('Error saving preferences:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update language
  const updateLanguage = async (newLanguage) => {
    return savePreferences(newLanguage, currency);
  };

  // Update currency
  const updateCurrency = async (newCurrency) => {
    return savePreferences(language, newCurrency);
  };

  // Convert amount from EUR to user's currency
  const convertAmount = useCallback((amountInEUR) => {
    if (amountInEUR === null || amountInEUR === undefined) return null;
    const rate = exchangeRates[currency] || 1;
    return amountInEUR * rate;
  }, [currency, exchangeRates]);

  // Convert amount from user's currency to EUR (for saving to DB)
  const convertToEUR = useCallback((amount) => {
    if (amount === null || amount === undefined) return null;
    const rate = exchangeRates[currency] || 1;
    return amount / rate;
  }, [currency, exchangeRates]);

  // Format amount with currency symbol
  const formatCurrency = useCallback((amountInEUR, options = {}) => {
    if (amountInEUR === null || amountInEUR === undefined) return '-';
    
    const { showSymbol = true, decimals = 2 } = options;
    const convertedAmount = convertAmount(amountInEUR);
    const symbol = CURRENCY_SYMBOLS[currency] || currency;
    
    // Format number based on language
    const formattedNumber = new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : 'en-US', {
      minimumFractionDigits: currency === 'XOF' ? 0 : decimals,
      maximumFractionDigits: currency === 'XOF' ? 0 : decimals
    }).format(convertedAmount);
    
    if (!showSymbol) return formattedNumber;
    
    // Position symbol based on currency
    if (currency === 'USD') {
      return `${symbol}${formattedNumber}`;
    } else if (currency === 'XOF') {
      return `${formattedNumber} ${symbol}`;
    } else {
      return `${formattedNumber} ${symbol}`;
    }
  }, [currency, language, convertAmount]);

  // Get translation
  const translate = useCallback((key) => {
    return t(language, key);
  }, [language]);

  // Get currency symbol
  const getCurrencySymbol = useCallback(() => {
    return CURRENCY_SYMBOLS[currency] || currency;
  }, [currency]);

  return (
    <PreferencesContext.Provider value={{
      language,
      currency,
      currencies,
      languages,
      exchangeRates,
      loading,
      updateLanguage,
      updateCurrency,
      savePreferences,
      convertAmount,
      convertToEUR,
      formatCurrency,
      getCurrencySymbol,
      t: translate
    }}>
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};
